import React, { useState, useEffect, useCallback } from "react";
import { api, fmtNumber } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Gauge, Loader2 } from "lucide-react";

// Renders the HubSpot API-call usage for the current session.
// - compact mode: a single meter card (used on the Connect tab under the banner).
//   Pass `usage` (from status.api_usage) OR let it fetch.
// - full mode: meter + per-source breakdown table (the "API Calls" sub-tab).
export default function ApiUsage({ compact = false, usage: usageProp }) {
  const [usage, setUsage] = useState(usageProp || null);
  const [loading, setLoading] = useState(!usageProp);

  const load = useCallback(async () => {
    if (usageProp !== undefined && usageProp !== null) {
      setUsage(usageProp);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const d = await api.getApiUsage().catch(() => null);
      setUsage(d);
    } catch (e) {
      logError("Failed to load API usage:", e);
    } finally {
      setLoading(false);
    }
  }, [usageProp]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep in sync if parent passes a fresh usage prop (Connect tab).
  useEffect(() => {
    if (usageProp !== undefined) setUsage(usageProp);
  }, [usageProp]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Unavailable (CSV or not captured).
  if (!usage || usage.available === false) {
    const reason = usage?.reason || "0 API calls (CSV upload — no HubSpot API used).";
    if (compact) {
      return (
        <div
          className="rounded-lg border border-border bg-muted/40 px-4 py-3 flex items-center gap-3"
          data-testid="api-usage-meter"
        >
          <Gauge className="w-5 h-5 text-muted-foreground flex-shrink-0" />
          <p className="text-sm text-muted-foreground">{reason}</p>
        </div>
      );
    }
    return (
      <Card data-testid="api-usage-unavailable">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Gauge className="w-4 h-4 text-primary" /> API Calls
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{reason}</p>
        </CardContent>
      </Card>
    );
  }

  const total = usage.total_calls ?? 0;
  const limit = usage.daily_limit || 250000;
  const pct = limit ? Math.min(100, (total / limit) * 100) : 0;
  const pctLabel = pct < 0.1 ? "<0.1" : pct.toFixed(pct < 1 ? 2 : 1);
  const breakdown = usage.breakdown || [];

  const meter = (
    <div
      className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-3"
      data-testid="api-usage-meter"
    >
      <div className="flex items-center gap-3">
        <Gauge className="w-5 h-5 text-primary flex-shrink-0" />
        <div className="text-sm">
          <span className="font-semibold text-foreground">{fmtNumber(total)}</span>
          <span className="text-muted-foreground"> HubSpot API calls used to connect &amp; pull your data.</span>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5">
        That is <span className="font-medium text-foreground">{pctLabel}%</span> of your portal&rsquo;s
        typical daily API allotment (~{fmtNumber(limit)} calls/day). HubSpot does not bill per call —
        this shows how much of your daily limit SalesTap consumed.
      </p>
    </div>
  );

  if (compact) return meter;

  return (
    <div className="space-y-6" data-testid="api-usage">
      <div className="flex items-center gap-2">
        <Gauge className="w-5 h-5 text-primary" />
        <div>
          <h2 className="font-semibold text-foreground leading-tight">API Calls</h2>
          <p className="text-xs text-muted-foreground">
            HubSpot API usage for this session, by data source — and which views each source feeds.
          </p>
        </div>
      </div>

      {meter}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Breakdown by Data Source</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto" data-testid="api-usage-breakdown">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="py-2 pr-4 font-medium text-muted-foreground">Data source</th>
                  <th className="py-2 pr-4 font-medium text-muted-foreground text-right">API calls</th>
                  <th className="py-2 font-medium text-muted-foreground">Feeds these views</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((row) => (
                  <tr key={row.key} className="border-b border-border/60">
                    <td className="py-2 pr-4 font-medium text-foreground">{row.label}</td>
                    <td className="py-2 pr-4 text-right font-semibold text-foreground">{fmtNumber(row.calls || 0)}</td>
                    <td className="py-2 text-xs text-muted-foreground">{(row.feeds || []).join(", ") || "—"}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-primary/40">
                  <td className="py-2 pr-4 font-semibold text-foreground">Total</td>
                  <td className="py-2 pr-4 text-right font-bold text-foreground">{fmtNumber(total)}</td>
                  <td className="py-2 text-xs text-muted-foreground">All views (from the one-time sync)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Note: SalesTap pulls all HubSpot data ONCE during Connect &amp; Sync, then computes every
            analytics view (Overview, Activity Insights, Pipeline, Forecast, Deals, Data Health) locally
            from that snapshot — so those views make <span className="font-medium">no additional</span>
            {" "}HubSpot API calls. CRM Inventory is the exception (it issues its own lightweight
            count-only calls).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
